/**
 * Feed settings panel (gear in the Feed header): sources, filters, ranking,
 * rotation. Edits stay a draft until "Save & reload" — the queue is rebuilt
 * once, on that press, not on every tick or keystroke.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatherIcon } from '../../components/FeatherIcon';
import type { CrmAccount } from '../../crm/crmAccess';
import { fetchOfLists, type OfList } from '../api';
import type { AccountColour } from '../colour';
import { AUTO_ADVANCE, CHAT_PERIODS, LIST_MAX_MEMBERS, SNOOZE_HOURS, SPEND_PERIODS, type FeedSettings } from '../feed';
import { AccountDropdown, ListDropdown } from './Dropdown';

interface Props {
  value: FeedSettings;
  accounts: CrmAccount[];
  colourOf: (accountId: string) => AccountColour;
  onChange: (next: FeedSettings) => void;
  onClose: () => void;
}

function Toggle({ on, onClick, label }: { on: boolean; onClick: () => void; label: string }) {
  return (
    <button type="button" className={`ws-toggle-row ${on ? 'is-on' : ''}`} onClick={onClick} aria-pressed={on}>
      <span className="ws-check">{on && <FeatherIcon name="check" size={10} />}</span>
      {label}
    </button>
  );
}

/** A reorderable on/off row (sources, ranking criteria). */
function OrderRow({ label, on, first, last, onToggle, onUp, onDown, upTip, downTip }: { label: string; on: boolean; first: boolean; last: boolean; onToggle: () => void; onUp: () => void; onDown: () => void; upTip: string; downTip: string }) {
  return (
    <div className={`ws-order-row ${on ? 'is-on' : ''}`}>
      <button type="button" className="ws-order-main" onClick={onToggle} aria-pressed={on}>
        <span className="ws-check">{on && <FeatherIcon name="check" size={10} />}</span>
        {label}
      </button>
      <button type="button" className="ws-icon-btn" disabled={first} title={upTip} onClick={onUp}>
        <FeatherIcon name="arrow-up" size={13} />
      </button>
      <button type="button" className="ws-icon-btn" disabled={last} title={downTip} onClick={onDown}>
        <FeatherIcon name="arrow-down" size={13} />
      </button>
    </div>
  );
}

function move<T>(list: T[], i: number, d: number): T[] {
  const j = i + d;
  if (j < 0 || j >= list.length) return list;
  const out = [...list];
  [out[i], out[j]] = [out[j]!, out[i]!];
  return out;
}

export function FeedSettingsPanel({ value: saved, accounts, colourOf, onChange, onClose }: Props) {
  const { t } = useTranslation('workspace');
  const [value, setValue] = useState(saved);
  const set = (patch: Partial<FeedSettings>) => setValue((v) => ({ ...v, ...patch }));
  const dirty = JSON.stringify(value) !== JSON.stringify(saved);
  // Start on the first Account that already has lists attached.
  const [listAccount, setListAccount] = useState(() => accounts.find((a) => saved.lists[a.account_id]?.length)?.account_id ?? accounts[0]?.account_id ?? '');
  const [lists, setLists] = useState<OfList[] | null>(null);

  useEffect(() => {
    if (!listAccount) return;
    let alive = true;
    setLists(null);
    fetchOfLists(listAccount)
      .then((l) => alive && setLists(l))
      .catch(() => alive && setLists([]));
    return () => {
      alive = false;
    };
  }, [listAccount]);

  const attached = value.lists[listAccount] || [];
  const toggleList = (l: Pick<OfList, 'id' | 'name'>) => {
    const on = attached.includes(l.id);
    const nextIds = on ? attached.filter((id) => id !== l.id) : [...attached, l.id];
    const lists = { ...value.lists };
    if (nextIds.length) lists[listAccount] = nextIds;
    else delete lists[listAccount];
    const listNames = { ...value.listNames };
    if (on) delete listNames[`${listAccount}:${l.id}`];
    else listNames[`${listAccount}:${l.id}`] = l.name;
    set({ lists, listNames });
  };
  const attachedCount = Object.values(value.lists).reduce((n, ids) => n + ids.length, 0);

  return (
    <div className="ws-menu ws-filters ws-feed-settings" onMouseDown={(e) => e.stopPropagation()}>
      <div className="ws-filters-head">
        <b>{t('feed.settings.title')}</b>
        <button type="button" className="ws-icon-btn" onClick={onClose} title={t('common.close')}>
          <FeatherIcon name="x" size={14} />
        </button>
      </div>
      <div className="ws-menu-scroll">
        <span className="ws-menu-section">{t('feed.settings.sources')}</span>
        <span className="ws-hint">{t('feed.settings.sourcesHint')}</span>
        {value.sources.map((s, i) => (
          <OrderRow
            key={s.id}
            label={t(`feed.sources.${s.id}`)}
            on={s.on}
            first={i === 0}
            last={i === value.sources.length - 1}
            upTip={t('feed.settings.moveUp')}
            downTip={t('feed.settings.moveDown')}
            onToggle={() => set({ sources: value.sources.map((x) => (x.id === s.id ? { ...x, on: !x.on } : x)) })}
            onUp={() => set({ sources: move(value.sources, i, -1) })}
            onDown={() => set({ sources: move(value.sources, i, 1) })}
          />
        ))}

        <span className="ws-menu-section">{t('feed.settings.minSpend')}</span>
        <label className="ws-amount">
          $
          <input
            inputMode="decimal"
            value={value.minSpend || ''}
            placeholder="0"
            onChange={(e) => set({ minSpend: Math.max(0, Number(e.target.value.replace(/[^\d.]/g, '')) || 0) })}
          />
        </label>
        <span className="ws-menu-section">{t('feed.settings.spendPeriod')}</span>
        <div className="ws-seg">
          {SPEND_PERIODS.map((p) => (
            <button key={p} type="button" className={value.spendPeriod === p ? 'is-on' : ''} onClick={() => set({ spendPeriod: p })}>
              {t(`feed.spendPeriod.${p}`)}
            </button>
          ))}
        </div>

        <span className="ws-menu-section">{t('feed.settings.lists', { count: attachedCount })}</span>
        <span className="ws-hint">{t('feed.settings.listsHint')}</span>
        {accounts.length > 0 && (
          <>
            <div className="ws-dd-stack">
              <AccountDropdown accounts={accounts} value={listAccount} onChange={setListAccount} colourOf={colourOf} badge={(id) => value.lists[id]?.length ?? 0} />
              <ListDropdown
                key={listAccount}
                lists={lists}
                isOn={(l) => attached.includes(l.id)}
                onToggle={toggleList}
                disabledTip={(l) => (l.members != null && l.members > LIST_MAX_MEMBERS ? t('feed.settings.listTooBig', { max: LIST_MAX_MEMBERS.toLocaleString('en-US') }) : null)}
              />
            </div>
            {attached.length > 0 && (
              <div className="ws-chips">
                {attached.map((id) => {
                  const name = lists?.find((l) => l.id === id)?.name ?? value.listNames[`${listAccount}:${id}`] ?? id;
                  return (
                    <button key={id} type="button" className="ws-chip is-on" title={t('dropdown.remove')} onClick={() => toggleList({ id, name })}>
                      {name}
                      <FeatherIcon name="x" size={11} />
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}

        <span className="ws-menu-section">{t('feed.settings.noChatIn')}</span>
        <div className="ws-seg">
          {CHAT_PERIODS.map((p) => (
            <button key={p} type="button" className={value.chatPeriod === p ? 'is-on' : ''} onClick={() => set({ chatPeriod: p })}>
              {t(`feed.chatPeriod.${p}`)}
            </button>
          ))}
        </div>

        <span className="ws-menu-section">{t('feed.settings.minBp')}</span>
        <div className="ws-seg">
          {[0, 1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" className={value.minBp === n ? 'is-on' : ''} onClick={() => set({ minBp: n })}>
              {n === 0 ? t('activity.filters.anyBp') : `${n}+`}
            </button>
          ))}
        </div>
        <span className="ws-menu-section">{t('feed.settings.minTemp')}</span>
        <div className="ws-seg">
          {[0, 1, 2, 3, 4].map((n) => (
            <button key={n} type="button" className={value.minTemp === n ? 'is-on' : ''} onClick={() => set({ minTemp: n })}>
              {n === 0 ? t('activity.filters.anyBp') : `${n}+`}
            </button>
          ))}
        </div>
        <Toggle on={value.includeUnknown} label={t('feed.settings.includeUnknown')} onClick={() => set({ includeUnknown: !value.includeUnknown })} />

        <span className="ws-menu-section">{t('feed.settings.rankBy')}</span>
        {value.rankBy.map((c, i) => (
          <OrderRow
            key={c.id}
            label={t(`feed.rank.${c.id}`)}
            on={c.on}
            first={i === 0}
            last={i === value.rankBy.length - 1}
            upTip={t('feed.settings.moveUp')}
            downTip={t('feed.settings.moveDown')}
            onToggle={() => set({ rankBy: value.rankBy.map((x) => (x.id === c.id ? { ...x, on: !x.on } : x)) })}
            onUp={() => set({ rankBy: move(value.rankBy, i, -1) })}
            onDown={() => set({ rankBy: move(value.rankBy, i, 1) })}
          />
        ))}
        <Toggle on={value.spread} label={t('feed.settings.spread')} onClick={() => set({ spread: !value.spread })} />
        <span className="ws-hint">{t('feed.settings.spreadHint')}</span>

        <span className="ws-menu-section">{t('feed.settings.autoAdvance')}</span>
        <div className="ws-seg">
          {AUTO_ADVANCE.map((n) => (
            <button key={n} type="button" className={value.autoAdvance === n ? 'is-on' : ''} onClick={() => set({ autoAdvance: n })}>
              {n === 0 ? t('feed.settings.off') : t('feed.settings.seconds', { count: n })}
            </button>
          ))}
        </div>
        <span className="ws-menu-section">{t('feed.settings.snooze')}</span>
        <div className="ws-seg">
          {SNOOZE_HOURS.map((h) => (
            <button key={h} type="button" className={value.snoozeHours === h ? 'is-on' : ''} onClick={() => set({ snoozeHours: h })}>
              {t('feed.settings.hours', { count: h })}
            </button>
          ))}
        </div>
      </div>
      <div className="ws-menu-foot">
        <button type="button" className="ws-btn ws-btn--ghost" onClick={onClose}>
          {t('layout.cancel')}
        </button>
        <button
          type="button"
          className="ws-btn ws-btn--primary"
          disabled={!dirty}
          onClick={() => {
            onChange(value);
            onClose();
          }}
        >
          <FeatherIcon name="refresh-cw" size={13} />
          {t('feed.settings.saveReload')}
        </button>
      </div>
    </div>
  );
}
